"use client";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Skeleton,
} from "@repo/ui";
import {
  ArrowLeftRight,
  Bell,
  Book,
  BookOpen,
  ChevronsUpDown,
  HelpCircle,
  Key,
  Landmark,
  MessageSquare,
  Settings,
} from "lucide-react";
import Link from "next/link";
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";
import { useEffect } from "react";

import { ClientStatusIndicator } from "@/components/client-status-indicator";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useCloudFrontUrl } from "@/hooks/use-cloudfront-url";
import { useMenuFocusReturn } from "@/hooks/use-menu-focus-return";
import { useSession } from "@/hooks/use-session";
import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/trpc/react";
import { cn } from "@/lib/utils";

/**
 * The menu is dark only where the surface that opens it is. The sidebar rail
 * paints itself with literal hex rather than a `.dark` class, so a menu that
 * portals out of it lands in a body still following the app theme and has to be
 * told. Every other caller (the marketing headers, the published-page header,
 * the internal top bar) sits on a light surface and must keep following it.
 * See `.theme-dark` in globals.css for why `dark` alone is not enough.
 */
const DARK_MENU_CLASS =
  "dark theme-dark border-[#1f1f1f] bg-[#0d0d0d] text-[#fafafa]";
const THEMED_MENU_CLASS =
  "dark:border-[#1f1f1f] dark:bg-[#0d0d0d] dark:text-[#fafafa]";

// Shared by the labelled tile and its loading state, which have to occupy the
// same box: the tile sits at the bottom of a column, so any height it gains on
// load shoves everything above it.
const TRIGGER_TILE_CLASS =
  "flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg transition-colors select-none";

interface UserNavProps {
  showFeedbackInDropdown?: boolean;
  showNotificationsInDropdown?: boolean;
  showDocsInDropdown?: boolean;
  isUserDashboard?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  showName?: boolean;
  /**
   * Widen `showName` into a two-line account row -- name over email, with a
   * menu affordance. For the mobile drawer, where the footer has the width to
   * spend and a bare avatar gives no clue whose account is signed in. The rail
   * variants stay one line: they have to survive collapsing to an icon.
   */
  showEmail?: boolean;
  /**
   * Restyle the bare-avatar trigger. Lets a surface that sits the avatar among
   * other controls -- the sidebar rail -- match their shape and hover instead
   * of standing out as the one round thing on the surface.
   */
  triggerClassName?: string;
  /**
   * Caption under the avatar, turning the bare photo into a labelled tile. For
   * the sidebar rail, where every other control names itself and an unlabelled
   * face reads as decoration rather than a menu.
   */
  triggerLabel?: string;
  /**
   * Gap between the trigger and the menu. A trigger inset from its surface's
   * edge -- a tile inside a padded sidebar footer -- needs to add that inset
   * back, or a `side="right"` menu opens on top of the rail it came from.
   */
  sideOffset?: number;
  /** Hold the menu dark whatever the app theme is. For dark surfaces only. */
  darkMenu?: boolean;
}

export function UserNav({
  showFeedbackInDropdown = false,
  showNotificationsInDropdown = false,
  showDocsInDropdown = false,
  isUserDashboard = false,
  side,
  align = "end",
  showName = false,
  showEmail = false,
  triggerClassName,
  triggerLabel,
  sideOffset = 4,
  darkMenu = false,
}: UserNavProps = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const { triggerProps, contentProps } = useMenuFocusReturn();
  const searchParams = useSearchParams();
  const params = useParams();
  const { user, isLoading } = useSession();
  const { data: activeOrganization } = authClient.useActiveOrganization();

  const orgSlug = params?.orgSlug as string | undefined;
  const { data: currentMember } = api.organization.getCurrentMember.useQuery(
    { organizationSlug: orgSlug ?? "" },
    {
      enabled: Boolean(orgSlug),
      staleTime: 5 * 60 * 1000,
    },
  );
  const orgEmail = currentMember?.orgEmail ?? null;

  // Fetch organizations separately - only when user is logged in
  const { data: organizations } =
    api.organization.getUserOrganizations.useQuery(undefined, {
      enabled: Boolean(user),
    });

  // Handle ?settings= query parameter for deep linking (e.g., from email preferences link)
  useEffect(() => {
    const settingsParam = searchParams?.get("settings");
    if (settingsParam) {
      // Map shorthand to redirect path
      const redirectMap: Record<string, string> = {
        settings: "/me/settings",
        finances: "/me/earnings",
        notifications: "/me/settings/notifications",
        "account-settings": "/me/settings",
        "account-finances": "/me/earnings",
        "account-notifications": "/me/settings/notifications",
      };

      const redirectPath = redirectMap[settingsParam];
      if (redirectPath) {
        // Clean up the URL first
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("settings");
        router.replace(newUrl.pathname + newUrl.search, { scroll: false });

        // Redirect to appropriate page
        router.push(redirectPath);
      }
    }
  }, [searchParams, router]);

  const { data: financialSummary } = api.user.getFinancialSummary.useQuery(
    undefined,
    { enabled: isUserDashboard },
  );

  // Generate CloudFront signed URL for user avatar
  const { url: userAvatarUrl } = useCloudFrontUrl(user?.image);

  if (!user && !isLoading) {
    return null;
  }

  const isLandingPage = pathname === "/" || pathname === "/home";
  const isInternalDashboard = pathname?.startsWith("/internal");
  const researcherDashboardSlug =
    activeOrganization?.slug ?? organizations?.[0]?.slug;
  const researcherDashboardHref = researcherDashboardSlug
    ? `/${researcherDashboardSlug}`
    : "/";
  const apiKeysOrgSlug = orgSlug ?? researcherDashboardSlug;

  const docsSubmenu = (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex cursor-pointer items-center gap-3 px-3 py-1.5">
        <div className="flex-1 font-medium">Docs</div>
        <Book className="size-4" />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuItem asChild>
          <Link
            href="/docs/experts"
            className="flex cursor-pointer items-center gap-3"
          >
            <div className="flex-1">
              <div className="font-medium">Expert Docs</div>
              <div className="text-muted-foreground text-xs">
                Guides for experts and panelists
              </div>
            </div>
            <BookOpen className="size-4" />
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/help" className="flex cursor-pointer items-center gap-3">
            <div className="flex-1">
              <div className="font-medium">Help Center</div>
              <div className="text-muted-foreground text-xs">
                Communities and direct support
              </div>
            </div>
            <HelpCircle className="size-4" />
          </Link>
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );

  return (
    <DropdownMenu>
      {/* Every branch below is a real button, because Radix hands focus back to
          whatever this renders when the menu closes: an `asChild` trigger that
          is a div takes the trigger props but cannot hold focus, so closing the
          menu drops it on the body and a keyboard user loses their place. */}
      <DropdownMenuTrigger asChild {...triggerProps}>
        {isLoading ? (
          showEmail ? (
            // Same two rows and same avatar as the loaded account row, so the
            // drawer footer holds its height across the swap.
            <button
              disabled
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl p-2"
            >
              <Skeleton className="size-9 shrink-0 rounded-lg bg-[#1f1f1f]" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-24 bg-[#1f1f1f]" />
                <Skeleton className="h-3 w-32 bg-[#1f1f1f]" />
              </div>
            </button>
          ) : triggerLabel ? (
            // The caption is static, so only the photo is unknown yet. Keeping
            // the real label means the tile is exactly its loaded height and
            // nothing above it moves when the session arrives.
            <button
              disabled
              className={cn(TRIGGER_TILE_CLASS, triggerClassName)}
            >
              <Skeleton className="size-9 shrink-0 rounded-lg bg-[#1f1f1f]" />
              <span className="block w-full truncate">{triggerLabel}</span>
            </button>
          ) : showName ? (
            // Mirror the named button exactly (p-2, size-5 avatar, flex-1 label)
            // so the footer never jumps between the loading and loaded states.
            <button
              disabled
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg p-2"
            >
              <Skeleton className="size-5 shrink-0 rounded-full bg-[#1f1f1f]" />
              <Skeleton className="h-4 flex-1 bg-[#1f1f1f] group-data-[collapsible=icon]:hidden" />
            </button>
          ) : (
            <button disabled>
              <Skeleton className="size-8 rounded-full" />
            </button>
          )
        ) : showEmail ? (
          <button className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-xl p-2 text-left transition-colors select-none [-webkit-tap-highlight-color:transparent] active:bg-white/[0.06]">
            <Avatar className="size-9 shrink-0 rounded-lg shadow-none">
              <AvatarImage
                src={userAvatarUrl ?? ""}
                alt={user?.name ?? "User"}
              />
              <AvatarFallback className="bg-accent text-foreground rounded-[inherit] text-sm font-medium">
                {user?.email?.[0]?.toUpperCase() ?? "U"}
              </AvatarFallback>
            </Avatar>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-[#fafafa]">
                {user?.name || "User"}
              </span>
              <span className="truncate text-xs text-[#8f8f8f]">
                {orgEmail ?? user?.email}
              </span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-[#8f8f8f]" />
          </button>
        ) : showName ? (
          <button className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-lg p-2 text-left transition-colors select-none hover:bg-[#171717]">
            <Avatar className="size-5 shrink-0 shadow-none">
              <AvatarImage
                src={userAvatarUrl ?? ""}
                alt={user?.name ?? "User"}
              />
              <AvatarFallback className="bg-accent text-foreground text-xs font-medium">
                {user?.email?.[0]?.toUpperCase() ?? "U"}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#fafafa] group-data-[collapsible=icon]:hidden">
              {user?.name || "User"}
            </span>
          </button>
        ) : triggerLabel ? (
          <button className={cn(TRIGGER_TILE_CLASS, triggerClassName)}>
            {/* Larger than the icons it sits under on purpose: a photograph
                carries far less contrast per pixel than a line glyph, and at
                icon size it stops reading as a face at all. */}
            <Avatar className="size-9 shrink-0 rounded-lg shadow-none">
              <AvatarImage
                src={userAvatarUrl ?? ""}
                alt={user?.name ?? "User"}
              />
              <AvatarFallback className="bg-accent text-foreground rounded-[inherit] text-xs font-medium">
                {user?.email?.[0]?.toUpperCase() ?? "U"}
              </AvatarFallback>
            </Avatar>
            <span className="block w-full truncate">{triggerLabel}</span>
          </button>
        ) : (
          <button className="rounded-full">
            <Avatar
              className={cn(
                "hover:border-primary hover:bg-accent size-8 cursor-pointer shadow-none transition-colors select-none",
                triggerClassName,
              )}
            >
              <AvatarImage
                src={userAvatarUrl ?? ""}
                alt={user?.name ?? "User"}
              />
              {/* Follows whatever corner the avatar is given, so a squared-off
                  trigger does not show a circular initial inside it. */}
              <AvatarFallback className="bg-accent text-foreground rounded-[inherit] font-medium">
                {user?.email?.[0]?.toUpperCase() ?? "U"}
              </AvatarFallback>
            </Avatar>
          </button>
        )}
      </DropdownMenuTrigger>
      {/* `dark theme-dark` carries the whole menu, not just this box, so the
          rows, separators and muted text inside stay dark on a light page
          without each restating a colour. See `.theme-dark` in globals.css. */}
      <DropdownMenuContent
        className={`w-72 rounded-lg shadow-sm ${darkMenu ? DARK_MENU_CLASS : THEMED_MENU_CLASS}`}
        align={align}
        side={side}
        sideOffset={sideOffset}
        {...contentProps}
      >
        <DropdownMenuItem asChild>
          <Link
            href="/me/settings"
            className="cursor-pointer px-3 py-2 font-normal"
          >
            <div className="flex items-center space-x-3">
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={userAvatarUrl ?? ""}
                  alt={user?.name ?? "User"}
                />
                <AvatarFallback className="bg-accent text-foreground font-medium">
                  {user?.email?.[0]?.toUpperCase() ?? "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col space-y-1">
                <p className="text-sm leading-none font-medium">
                  {user?.name || "User"}
                </p>
                <p className="text-muted-foreground text-xs leading-none">
                  {orgEmail ?? user?.email}
                </p>
              </div>
            </div>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        {isUserDashboard ? (
          // User Dashboard Dropdown
          <>
            <DropdownMenuItem asChild>
              <Link
                href="/me/earnings"
                className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
              >
                <div className="flex-1">
                  <div className="text-lg font-medium">
                    $
                    {financialSummary?.currentBalance?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) ?? "0.00"}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    View your finances & payouts
                  </div>
                </div>
                <Landmark className="size-4" />
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link
                href="/me/settings/notifications"
                className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
              >
                <div className="flex-1">
                  <div className="font-medium">Notification Preferences</div>
                </div>
                <Bell className="size-4" />
              </Link>
            </DropdownMenuItem>

            {/* Conditional components in dropdown */}
            {showFeedbackInDropdown && (
              <DropdownMenuItem asChild>
                <a
                  href="https://terac.com/interview/start/bb8b4b28f6f27762d69e7d79caf80a53-8fefaf9a-1211-4bc1-a40a-de19ec4db5aa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1">
                    <div className="font-medium">Feedback</div>
                  </div>
                  <MessageSquare className="size-4" />
                </a>
              </DropdownMenuItem>
            )}

            {showNotificationsInDropdown && (
              <DropdownMenuItem asChild>
                <Link
                  href="/notifications"
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1">
                    <div className="font-medium">Notifications</div>
                  </div>
                  <MessageSquare className="size-4" />
                </Link>
              </DropdownMenuItem>
            )}

            {showDocsInDropdown && docsSubmenu}

            <DropdownMenuSeparator />

            <div className="flex items-center justify-between px-3 py-1.5">
              <div className="text-sm font-medium">Theme</div>
              <ThemeToggle />
            </div>

            <DropdownMenuSeparator />

            <div className="px-3 py-1.5">
              <ClientStatusIndicator />
            </div>

            {/* Only an expert who ALREADY has an organization is offered the
                switch. An expert without one used to be offered the org-setup
                wizard here, and it converted experts who came to this menu
                looking for their profile into researchers; the researcher side
                is entered from the researcher surfaces instead. Waits for the
                query: rendering nothing beats flickering the wrong item. */}
            {organizations && organizations.length > 0 && (
              <>
                <DropdownMenuSeparator />

                <DropdownMenuItem asChild>
                  <Link
                    href={`/${activeOrganization?.slug ?? organizations[0]?.slug ?? ""}`}
                    className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                  >
                    <div className="flex-1 font-medium">
                      Switch to Researcher
                    </div>
                    <ArrowLeftRight className="size-4" />
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            {isLandingPage && (
              <DropdownMenuItem asChild>
                <Link
                  href="/me"
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1 font-medium">Expert Dashboard</div>
                  <ArrowLeftRight className="size-4" />
                </Link>
              </DropdownMenuItem>
            )}
          </>
        ) : (
          // Regular Dropdown
          <>
            <DropdownMenuItem asChild>
              <Link
                href="/me/settings"
                className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
              >
                <div className="flex-1">
                  <div className="font-medium">Account Settings</div>
                </div>
                <Settings className="size-4" />
              </Link>
            </DropdownMenuItem>

            {apiKeysOrgSlug && (
              <DropdownMenuItem asChild>
                <Link
                  href={`/${apiKeysOrgSlug}/settings/developers`}
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1">
                    <div className="font-medium">API Keys</div>
                  </div>
                  <Key className="size-4" />
                </Link>
              </DropdownMenuItem>
            )}

            {!isLandingPage && (
              <DropdownMenuItem asChild>
                <Link
                  href="/home"
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1">
                    <div className="font-medium">Home Page</div>
                  </div>
                  <span className="text-muted-foreground">
                    <Logo
                      width={16}
                      height={16}
                      color="currentColor"
                      thickness={4}
                    />
                  </span>
                </Link>
              </DropdownMenuItem>
            )}

            {/* Conditional components in dropdown */}
            {showFeedbackInDropdown && (
              <DropdownMenuItem asChild>
                <a
                  href="https://terac.com/interview/start/bb8b4b28f6f27762d69e7d79caf80a53-8fefaf9a-1211-4bc1-a40a-de19ec4db5aa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1">
                    <div className="font-medium">Feedback</div>
                  </div>
                  <MessageSquare className="size-4" />
                </a>
              </DropdownMenuItem>
            )}

            {showNotificationsInDropdown && (
              <DropdownMenuItem asChild>
                <Link
                  href="/notifications"
                  className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                >
                  <div className="flex-1">
                    <div className="font-medium">Notifications</div>
                  </div>
                  <MessageSquare className="size-4" />
                </Link>
              </DropdownMenuItem>
            )}

            {showDocsInDropdown && docsSubmenu}

            <DropdownMenuSeparator />

            <div className="flex items-center justify-between px-3 py-1.5">
              <div className="text-sm font-medium">Theme</div>
              <ThemeToggle />
            </div>

            <DropdownMenuSeparator />

            <div className="px-3 py-1.5">
              <ClientStatusIndicator />
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link
                href="/me"
                className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
              >
                <div className="flex-1 font-medium">Switch to Expert</div>
                <ArrowLeftRight className="size-4" />
              </Link>
            </DropdownMenuItem>
            {(isLandingPage || isInternalDashboard) &&
              organizations &&
              organizations.length > 0 && (
                <DropdownMenuItem asChild>
                  <Link
                    href={researcherDashboardHref}
                    className="flex cursor-pointer items-center gap-3 px-3 py-1.5"
                  >
                    <div className="flex-1 font-medium">
                      Switch to Researcher
                    </div>
                    <ArrowLeftRight className="size-4" />
                  </Link>
                </DropdownMenuItem>
              )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
